import { Box, Button } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import selectOptions from '../../constants/selectOptions'
import useDebounce from '../../hooks/useDebounce'
import { updateFilterObjectCheckedValue } from '../../pages/ExportPage'
import materialsService from '../../services/materialsService'
import reportsSelectors from '../../store/reports/reportsSelectors'
import usersSelectors from '../../store/users/usersSelectors'
import { IGenericObjectStringValue } from '../../types'
import ButtonsGroup from '../ButtonsGroup'
import CardTAG from '../CardTAG'
import AccordionTimeline from '../CaseTimeline/AccordionTimeline'
import Chips from '../Chips'
import DividerTAG from '../DividerTAG'
import Dropdown from '../Dropdown'
import { IFilterObject } from '../FilterComponent'
import FormLayout from '../forms/FormLayout'
import PageBodyLayout from '../PageBodyLayout'
import ReportSubheader from '../ReportSubheader'
import constants from './constants'
import AttachmentsCaseDataPart from './DataPart'
import { IFile } from './types'
import utils from './utils'
import materialSelectors from '../../store/materials/materialSelectors'
import appConfigSelector from '../../store/appConfig/appConfigSelector'
import { AppDispatch } from '../../store'
import { caseAttachmentsViewChange, update } from '../../store/appConfig/appConfigSlice'

import { error } from '../../utils/alertUtils';


const getSelectedValues = (filters: IFilterObject[]) => {
  const selectedValues: { [key: string]: string } = {}

  filters.forEach(filter => {
    if (filter.section) {
      const checkedInSection = filter.subTypes
        ?.filter(subType => subType.checked)
        .map(type => type.value)
        .join()

      if (checkedInSection?.length) {
        selectedValues[filter.value] = checkedInSection
      }
    }
  })

  return selectedValues
}

const AttachmentsCase: FC = () => {
  const viewType = useSelector(appConfigSelector.caseAttachmentsView)
  const [files, setFiles] = useState<IFile[]>([])
  const [filter, setFilter] = useState<IFilterObject[]>(constants.defaultFilter)
  const debouncedFilterValue = useDebounce(filter, 500)
  const [selectedValues, setSelectedValues] = useState<{ label: string; value: string }[]>([])
  const dispatch = useDispatch<AppDispatch>()
  const teamMembers = useSelector(usersSelectors.teamMembers)
  const selectedReport = useSelector(reportsSelectors.selectedReport)
  const additionalFiles = useSelector(materialSelectors.getAdditionalMaterials)
  const formMethods = useForm<IGenericObjectStringValue>({
    defaultValues: { ordering: 'created_at', jumpTo: '' },
    mode: 'onChange',
    shouldFocusError: false,
  })
  const { control, watch } = formMethods
  const orderingWatchValue = watch('ordering')

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, section: string) => {
    const response = updateFilterObjectCheckedValue(filter, [event?.target?.value])
    if (response) {
      setFilter(response)
    }

    if (!event?.target?.checked) {
      const filteredOptions = selectedValues.filter(option => option?.value !== event?.target.value)
      setSelectedValues(filteredOptions)
      return
    }
    setSelectedValues([
      ...selectedValues,
      {
        label: `${section}: ${event?.target?.value}`,
        value: event?.target?.value,
      },
    ])
  }

  const removeChipHandler = (value: string) => {
    const response = updateFilterObjectCheckedValue(filter, [value])
    if (response) {
      setFilter(response)
    }
    const filteredOptions = selectedValues.filter(option => option?.value !== value)
    setSelectedValues(filteredOptions)
  }

  const renderFilterSections = (filters: IFilterObject[]) => {
    return filters.map(filter => {
      if (filter?.section) {
        return (
          <AccordionTimeline title={filter?.title} key={filter?.value}>
            <FormLayout gap={'0'}>
              {utils.renderCheckboxes(filter?.subTypes ?? [], onChangeHandler, filter?.value)}
            </FormLayout>
          </AccordionTimeline>
        )
      }
    })
  }

  const resetFilter = () => {
    setSelectedValues([])
    setFilter(constants.defaultFilter)
  }

  useEffect(() => {
    if (teamMembers && teamMembers?.length > 0) {
      const formatedTeam = teamMembers?.map(member => {
        return {
          title: `${member?.user?.first_name} ${member?.user?.last_name} `,
          value: `${member?.id}`,
          checked: false,
          section: false,
        } as IFilterObject
      })
      setFilter([
        ...filter,
        {
          title: 'Updates By Person',
          value: 'user',
          section: true,
          checked: false,
          subTypes: formatedTeam,
        },
      ])
    }
  }, [teamMembers])

  const filterData = async (filter: any) => {
    try {
      const { data } = await materialsService.filterFiles(selectedReport?.id ?? 1, filter)
      setFiles(data)
    } catch (err) {
      error('Error fetching files')
    }
  }

  useEffect(() => {
    if (selectedReport)
      filterData({
        ...getSelectedValues(debouncedFilterValue),
      })
  }, [selectedReport, debouncedFilterValue, orderingWatchValue, additionalFiles])

  useEffect(() => {
    const viewTypeLocalStorageItem = localStorage.getItem('viewType')
    const viewTypeObject = viewTypeLocalStorageItem && JSON.parse(viewTypeLocalStorageItem)
    dispatch(update(viewTypeObject))
  }, [])

  return (
    <PageBodyLayout
      className='t-case-attachment-page'
      caseSubheader={<ReportSubheader />}
      dataPart={<AttachmentsCaseDataPart viewType={viewType} reportAdditionalMaterials={files} />}
      filterPart={
        <CardTAG title='Filter'>
          <div className='t-filter-row t-hbox t-align-items-center t-gap-36'>
            <Dropdown
              control={control}
              label='Sort by'
              options={selectOptions.ascedingDescendingOptions}
              fieldName='ordering'
              selectedValue={orderingWatchValue}
            />
            <div className='t-view-as t-vbox'>
              <span className='t-font-bold t-font-size-small'>View as</span>
              <ButtonsGroup
                selectedValue={viewType}
                firstButtonLabel='Tiles'
                secondButtonLabel='List'
                firstButtonClickHandler={() => {
                  dispatch(caseAttachmentsViewChange('box'))
                }}
                secondButtonClickHandler={() => {
                  dispatch(caseAttachmentsViewChange('list'))
                }}
              />
            </div>
          </div>
          {selectedValues?.length > 0 ? (
            <Box className={'t-chips-outer-wrap t-vbox'}>
              <div className='t-chip-control t-vbox'>
                <span className='t-chips-label t-font-bold t-font-size-small'>Current Presets</span>
                <Chips
                  values={selectedValues}
                  handleRemove={removeChipHandler}
                  customChipStyle={constants.customChipStyle}
                />
              </div>
              <Button variant='dashed' onClick={resetFilter}>
                Clear All
              </Button>
            </Box>
          ) : null}
          <div className='t-vbox' style={{ gap: '1.8rem' }}>
            <DividerTAG />
            {renderFilterSections(filter)}
          </div>
        </CardTAG>
      }
    />
  )
}

export default AttachmentsCase
